package web.fstech.onthecoffee

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import web.fstech.onthecoffee.privacy.*
import web.fstech.onthecoffee.resources.AppStrings

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrivacyPolicyPage(
    onBack: () -> Unit,
    language: AppLanguage = AppLanguage.ENGLISH,
    onLanguageChange: (AppLanguage) -> Unit = {}
) {
    val scrollState = rememberScrollState()
    val strings = AppStrings.get(language)

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("${strings.navPrivacyPolicy} - ${strings.appName}") },
                navigationIcon = {
                    Button(onClick = onBack, modifier = Modifier.padding(horizontal = 8.dp)) {
                        Text(strings.navHome)
                    }
                },
                actions = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.padding(end = 12.dp)
                    ) {
                        FilterChip(
                            selected = language == AppLanguage.ENGLISH,
                            onClick = { onLanguageChange(AppLanguage.ENGLISH) },
                            label = { Text("English") }
                        )
                        FilterChip(
                            selected = language == AppLanguage.ARABIC,
                            onClick = { onLanguageChange(AppLanguage.ARABIC) },
                            label = { Text("العربية") }
                        )
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(scrollState)
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (language == AppLanguage.ARABIC) {
                ArabicPrivacyPolicyContent()
            } else {
                EnglishPrivacyPolicyContent()
            }
        }
    }
}

@Composable
private fun EnglishPrivacyPolicyContent() {
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("📜 Official Google Play & App Store Compliant Document", style = MaterialTheme.typography.titleMedium)
            Text("Package Name: client.digital.onthecoffee | App Name: OnTheCoffee (على القهوة)", style = MaterialTheme.typography.bodySmall)
            Text("Last Updated: March 30, 2025 | Effective Date: March 30, 2025", style = MaterialTheme.typography.bodySmall)
        }
    }

    Text("Privacy Policy for OnTheCoffee (على القهوة)", style = MaterialTheme.typography.headlineLarge)

    Text(
        "Welcome to OnTheCoffee (على القهوة) (\"we,\" \"our,\" or \"us\"). We respect your privacy and are committed to protecting the personal data of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.",
        style = MaterialTheme.typography.bodyLarge
    )

    HorizontalDivider()

    Text("1. Information We Collect", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• Personal Data You Provide: Account Information (Email, Google Sign-In, user ID, profile details, business ideas, Business Model Canvas inputs, game scores, leaderboard achievements).\n" +
                "• Automatically Collected Information: Device model, OS version, language preference, Google Advertising ID (com.google.android.gms.permission.AD_ID), IP address, Firebase Cloud Messaging push notification tokens, and crash diagnostics.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("2. How We Use Your Information", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• Core Application Functionality: User authentication, saving game progress, managing business models, displaying leaderboards.\n" +
                "• AI-Powered Business Canvas Generation: Business idea evaluations via Google Gemini AI models on Supabase Edge Functions.\n" +
                "• Monetization & Advertising: Non-intrusive banner and rewarded video ads via Google AdMob and privacy consent via Google UMP SDK.\n" +
                "• Notifications & Improvements: Delivering updates and optimizing security.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("3. Third-Party Services & Analytics", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• Supabase (Backend & PostgreSQL Database)\n" +
                "• Google Gemini AI (Business Idea Processing)\n" +
                "• Google Firebase (Analytics & Push Notifications)\n" +
                "• Google AdMob & UMP SDK (Advertising & Privacy Consent)\n" +
                "• Google Play Billing (In-App Purchases)",
        style = MaterialTheme.typography.bodyMedium
    )

    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("🔒 AI Data Processing (Google Gemini)", style = MaterialTheme.typography.titleMedium)
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "When you submit a business idea for evaluation in the Business Arena, data is encrypted via HTTPS and sent to our Supabase Edge Function to interface with Google Gemini AI. Your proprietary ideas are strictly used for your Business Model Canvas generation, are never sold, and are NEVER used to train public AI models.",
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }

    Text("4. Account Deletion & User Rights (Google Play Data Safety)", style = MaterialTheme.typography.headlineMedium)
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("🗑️ Permanent Account & Data Deletion Request", style = MaterialTheme.typography.titleMedium)
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "You have the right to request permanent deletion of your account and all associated data (projects, game records, saved favorites) at any time:\n" +
                        "1. In-App: Navigate to Profile > Settings > Delete Account.\n" +
                        "2. Web Portal: Visit our Account Management section (/delete_account).\n" +
                        "3. Email Request: Email us at support@onthecoffee.digital or sftech_contact@yahoo.com with 'Account Deletion Request'.",
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }

    Text("5. Children's Privacy (COPPA)", style = MaterialTheme.typography.headlineMedium)
    Text(
        "OnTheCoffee is intended for general audiences. We do not knowingly collect personal information from children under 13. AdMob settings include tagForChildDirectedTreatment and appropriate content rating filters.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("6. Contact Us", style = MaterialTheme.typography.headlineMedium)
    Text(
        "If you have questions regarding this Privacy Policy, please contact our support team:\n" +
                "• Application Name: OnTheCoffee (على القهوة)\n" +
                "• Package Identifier: client.digital.onthecoffee\n" +
                "• Support Email: support@onthecoffee.digital / sftech_contact@yahoo.com",
        style = MaterialTheme.typography.bodyMedium
    )
}

@Composable
private fun ArabicPrivacyPolicyContent() {
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("📜 وثيقة رسمية متوافقة مع متطلبات متجر جوجل بلاي وآبل", style = MaterialTheme.typography.titleMedium)
            Text("معرف التطبيق: client.digital.onthecoffee | اسم التطبيق: على القهوة (OnTheCoffee)", style = MaterialTheme.typography.bodySmall)
            Text("تاريخ آخر تحديث: 30 مارس 2025", style = MaterialTheme.typography.bodySmall)
        }
    }

    Text("سياسة الخصوصية لتطبيق «على القهوة» (OnTheCoffee)", style = MaterialTheme.typography.headlineLarge)

    Text(
        "مرحباً بكم في تطبيق «على القهوة» (OnTheCoffee). نحن نولي اهتماماً بالغاً لخصوصية بياناتكم الشخصية ونلتزم بحمايتها وفق أعلى معايير الأمان المعتمدة. توضح هذه الوثيقة كيفية جمع واستخدام وحماية بياناتكم عند استخدام التطبيق والموقع الإلكتروني.",
        style = MaterialTheme.typography.bodyLarge
    )

    HorizontalDivider()

    Text("1. البيانات التي نجمعها", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• بيانات الحساب: البريد الإلكتروني، الاسم، معرّف المستخدم المدار عبر Supabase وتسجيل الدخول بواسطة Google.\n" +
                "• المحتوى والأنشطة: تفاصيل أفكار المشاريع الريادية، نماذج العمل التجاري (BMC)، نتائج التحديات والألعاب، ولوحة الشرف.\n" +
                "• بيانات الجهاز والتقنية: نوع الجهاز، نظام التشغيل، معرّف الإعلانات (com.google.android.gms.permission.AD_ID)، عنوان IP، ورموز الإشعارات عبر Firebase.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("2. كيفية استخدام البيانات", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• تشغيل التطبيق الأساسي: إدارة الحسابات، حفظ تقدم الألعاب، ولوحة الشرف.\n" +
                "• الذكاء الاصطناعي: توليد وتحليل نموذج العمل التجاري (BMC) عبر Google Gemini AI.\n" +
                "• الإعلانات والاشتراكات: عرض إعلانات ملائمة عبر Google AdMob وتأكيد موافقة الخصوصية عبر Google UMP SDK.\n" +
                "• التنبيهات والأمان: إرسال التنبيهات وحماية الحسابات.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("3. الخدمات والشركاء من الأطراف الثالثة", style = MaterialTheme.typography.headlineMedium)
    Text(
        "• Supabase (إدارة قاعدة البيانات والهوية)\n" +
                "• Google Gemini AI (معالجة أفكار المشاريع)\n" +
                "• Google Firebase (التحليلات والإشعارات)\n" +
                "• Google AdMob & UMP SDK (الإعلانات وموافقة الخصوصية)\n" +
                "• Google Play Billing (عمليات الشراء والاشتراكات)",
        style = MaterialTheme.typography.bodyMedium
    )

    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("🔒 معالجة أفكار الأعمال والذكاء الاصطناعي (Google Gemini)", style = MaterialTheme.typography.titleMedium)
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "يتم إرسال أفكار المشاريع المشفرة عبر بروتوكول HTTPS إلى خوادم Supabase Edge Functions لمعالجتها بواسطة نموذج Google Gemini AI حصراً لتوليد نموذج BMC. نحن لا نبيع أفكارك التجارية ولا نستخدمها لتدريب نماذج ذكاء اصطناعي عامة.",
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }

    Text("4. حذف الحساب وحقوق المستخدم (سلامة البيانات - Google Play)", style = MaterialTheme.typography.headlineMedium)
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("🗑️ حق حذف الحساب والبيانات نهائياً", style = MaterialTheme.typography.titleMedium)
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "يحق لك في أي وقت حذف حسابك وكافة البيانات المرتبطة به نهائياً:\n" +
                        "1. من داخل التطبيق: الانتقال إلى الملف الشخصي > الإعدادات > حذف الحساب.\n" +
                        "2. عبر موقعنا: زيارة قسم إدارة الحسابات (/delete_account).\n" +
                        "3. عبر البريد الإلكتروني: إرسال طلب إلى support@onthecoffee.digital أو sftech_contact@yahoo.com.",
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }

    Text("5. خصوصية الأطفال (COPPA)", style = MaterialTheme.typography.headlineMedium)
    Text(
        "التطبيق مخصص لجمهور عام. نطبق إعدادات حماية الأطفال تقييد تصنيف محتوى الإعلانات لحماية فئات المستخدمين الصغار.",
        style = MaterialTheme.typography.bodyMedium
    )

    Text("6. اتصل بنا", style = MaterialTheme.typography.headlineMedium)
    Text(
        "لأي استفسار بخصوص سياسة الخصوصية، يرجى التواصل مع فريق الدعم:\n" +
                "• اسم التطبيق: على القهوة (OnTheCoffee)\n" +
                "• معرّف التطبيق: client.digital.onthecoffee\n" +
                "• البريد الإلكتروني: support@onthecoffee.digital / sftech_contact@yahoo.com",
        style = MaterialTheme.typography.bodyMedium
    )
}
